import * as React from 'react';
import { cn } from './cn';
import type { EdsTone } from './types';

const TONE: Record<EdsTone, string> = {
  neutral: 'bg-eds-muted text-eds-fg',
  primary: 'bg-eds-primary/12 text-eds-emerald-2',
  info: 'bg-eds-info/12 text-eds-info',
  success: 'bg-eds-success/12 text-eds-emerald-2',
  warning: 'bg-eds-warning/15 text-eds-earth',
  danger: 'bg-eds-danger/12 text-eds-danger',
  gold: 'bg-eds-gold/15 text-eds-earth',
};

const SOLID: Record<EdsTone, string> = {
  neutral: 'bg-eds-muted text-eds-fg',
  primary: 'bg-eds-primary text-eds-primary-fg',
  info: 'bg-eds-info text-eds-info-fg',
  success: 'bg-eds-success text-eds-success-fg',
  warning: 'bg-eds-warning text-eds-warning-fg',
  danger: 'bg-eds-danger text-eds-danger-fg',
  gold: 'bg-eds-gold text-eds-warning-fg',
};

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  tone?: EdsTone;
  /** Solid fill (default is a soft tinted chip). */
  solid?: boolean;
  /** Leading status dot. */
  dot?: boolean;
  icon?: React.ReactNode;
}

/**
 * EDS Badge / status chip — Design Bible §2.3, §7.
 * Status is never colour-only: pair with an icon or dot + text label.
 */
export const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, tone = 'neutral', solid = false, dot = false, icon, children, ...props }, ref) => (
    <span
      ref={ref}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-eds-pill px-2.5 py-0.5',
        'font-eds-body text-xs font-semibold whitespace-nowrap',
        solid ? SOLID[tone] : TONE[tone],
        className,
      )}
      {...props}
    >
      {dot && <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true" />}
      {icon}
      {children}
    </span>
  ),
);
Badge.displayName = 'EdsBadge';

export default Badge;
